import api from '@/services/api';
import type {
    DealerFundStatement,
    DealerFundStatementPage,
    DealerFundStatementResult,
    DealerFundStatementRow,
} from '../types/dealerFundStatement.types';
import { hasApiCode, NOT_ENTITLED_CODE, payloadReaders, UNVERIFIED_CODE } from './statementPayloadReaders';

export const DEALER_FUND_STATEMENT_PER_PAGE = 50;

const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

const { malformed, readRecord, readText, readNullableText, readMoney, readInt, readCurrency, readArray } =
    payloadReaders('fund statement payload');

const readTimestamp = (value: unknown, label: string): string => {
    const text = readText(value, label);
    if (!ISO_TIMESTAMP.test(text) || Number.isNaN(Date.parse(text))) throw malformed(`${label} is not a timestamp`);
    return text;
};

const mapRow = (value: unknown): DealerFundStatementRow => {
    const raw = readRecord(value, 'agreement row');
    return {
        agreementId: readText(raw.agreement_id, 'agreement_id'),
        sellingDealerId: readText(raw.selling_dealer_id, 'selling_dealer_id'),
        productId: readNullableText(raw.product_id, 'product_id'),
        campaignId: readNullableText(raw.campaign_id, 'campaign_id'),
        openedAt: readTimestamp(raw.opened_at, 'opened_at'),
        balance: readMoney(raw.balance, 'balance'),
        remainingGross: readMoney(raw.remaining_gross, 'remaining_gross'),
    };
};

const mapMeta = (value: unknown): DealerFundStatementPage => {
    const raw = readRecord(value, 'meta');
    return {
        page: readInt(raw.page, 'meta page', 1),
        perPage: readInt(raw.per_page, 'meta per_page', 1),
        total: readInt(raw.total, 'meta total', 0),
        lastPage: readInt(raw.last_page, 'meta last_page', 1),
    };
};

const mapStatement = (data: unknown, meta: unknown, requestedDealerId: string): DealerFundStatement => {
    const raw = readRecord(data, 'root');
    const dealerId = readText(raw.dealer_id, 'dealer_id');
    if (dealerId.toLowerCase() !== requestedDealerId.toLowerCase()) throw malformed('dealer_id does not match the request');
    if (raw.scale !== 2) throw malformed('scale is not 2');
    readText(raw.membership_as_of, 'membership_as_of');

    const page = mapMeta(meta);
    const accountCount = readInt(raw.account_count, 'account_count', 0);
    if (accountCount !== page.total) throw malformed('account_count does not match meta total');

    return {
        dealerId,
        currency: readCurrency(raw.currency),
        roundingMode: readText(raw.rounding_mode, 'rounding_mode'),
        total: readMoney(raw.total, 'total'),
        accountCount,
        agreements: readArray(raw.agreements, 'agreements').map(mapRow),
        meta: page,
    };
};

export const fetchDealerFundStatement = async (
    dealerId: string,
    page: number,
    perPage: number = DEALER_FUND_STATEMENT_PER_PAGE,
): Promise<DealerFundStatementResult> => {
    try {
        const response = await api.get(`/dealers/${dealerId}/fund-statement`, {
            params: { page, per_page: perPage },
            suppressGlobalForbiddenToast: true,
        });
        return { status: 'ok', statement: mapStatement(response.data?.data, response.data?.meta, dealerId) };
    } catch (error) {
        if (hasApiCode(error, 503, UNVERIFIED_CODE)) return { status: 'unverified' };
        if (hasApiCode(error, 403, NOT_ENTITLED_CODE)) return { status: 'not_entitled' };
        throw error;
    }
};
